import { CRISIS_DIRECTOR } from './crises';
import type { BuildingId, TechBranchId, TechId } from './ids';
import type { TradeGoodId } from './market';

/**
 * The technology tree (GDD §10; approved in «Дерево технологий · MVP»).
 * Science piles up in a stock and flows each cycle into the technologies
 * being researched. Tier 3 and the final technology also cost exotics.
 * Every technology opens a decision or a building block, not just a percent
 * (GDD §4). Unlocks that live on other content (buildings, ship classes,
 * cloud levels, levers, invasion) point back here through `requiresTech`;
 * the rest is in `effects`. Starting values for tuning.
 */
export type TechTier = 1 | 2 | 3 | 'final';

/** What a technology changes. Bonuses add up between technologies; factors multiply. */
export interface TechEffects {
  /** Workers per level, added to these buildings' jobs (never below 0). */
  readonly jobsPerLevel?: Readonly<Partial<Record<BuildingId, number>>>;
  /** Share added to these buildings' outputs, extraction and science. */
  readonly buildingOutput?: Readonly<Partial<Record<BuildingId, number>>>;
  /** Factor on these buildings' inputs. */
  readonly buildingInputs?: Readonly<
    Partial<Record<BuildingId, Readonly<Partial<Record<TradeGoodId, number>>>>>
  >;
  /** Factor on helium-3 burnt per unit of reactor energy. */
  readonly heliumPerEnergy?: number;
  /** Extra orbital slots. */
  readonly orbitSlots?: number;
  /** Extra surface slots (#79). */
  readonly surfaceSlots?: number;
  /** Share added to population growth. */
  readonly populationGrowth?: number;
  /** Share added to every outpost's output. */
  readonly outpostOutput?: number;
  /** Factor on exotics from finds and Lagrange stations. */
  readonly exoticsYield?: number;
  /** Extra contract slots (with a commerce center). */
  readonly contractSlots?: number;
  /** Share added to caravan speed. */
  readonly caravanSpeed?: number;
  /** The player's own conversion fee on the exchange (replaces CURRENCY.conversionFee). */
  readonly conversionFee?: number;
  /** The issuer's seigniorage while its currency is the reserve (replaces RESERVE.seigniorage). */
  readonly seigniorage?: number;
  /** Factor on the time of each find. */
  readonly scanTime?: number;
  /** Factor on fleet upkeep. */
  readonly fleetUpkeep?: number;
  /** Extra ships within the fleet limit. */
  readonly fleetLimit?: number;
  /** Extra warning before a crisis, ticks, with the exact place of the strike (#29). */
  readonly crisisWarningTicks?: number;
  /** Factor on crisis damage to own buildings and ships (#29). */
  readonly crisisDamage?: number;
  /** Share added to the Prestige of contributions to joint projects (#30). */
  readonly projectPrestige?: number;
  /** Allows building the megastructure of the technology victory (#30). */
  readonly megastructure?: boolean;
  /** The whole system explored at once (#97): every hex under the fog becomes known. */
  readonly revealMap?: boolean;
  /** Trading rules per good run every cycle on the exchange (#123). */
  readonly autoTrade?: boolean;
  /** Ships may be merged into fleets, with the bonuses of a formation (#160). */
  readonly formations?: boolean;
}

export interface TechDef {
  readonly id: TechId;
  /** i18n key: `tech.<id>.name`. */
  readonly i18nKey: `tech.${TechId}`;
  readonly branch: TechBranchId;
  readonly tier: TechTier;
  /** Technologies it needs... */
  readonly requires: readonly TechId[];
  /** ...at least this many of them (all, unless the final asks for any two). */
  readonly requiresCount: number;
  readonly effects: TechEffects;
}

const tech = (
  id: TechId,
  branch: TechBranchId,
  tier: TechTier,
  requires: readonly TechId[],
  effects: TechEffects = {},
  requiresCount = requires.length,
): TechDef => ({ id, i18nKey: `tech.${id}`, branch, tier, requires, requiresCount, effects });

/** Production buildings: «Автоматизация» frees a worker per level in each. */
const PRODUCERS = ['agroComplex', 'miningComplex', 'smelter', 'electronicsFactory'] as const;

export const TECHS: Readonly<Record<TechId, TechDef>> = {
  // Industry
  automation: tech('automation', 'industry', 1, [], {
    jobsPerLevel: Object.fromEntries(PRODUCERS.map((b) => [b, -1])),
    surfaceSlots: 1,
  }),
  deepMining: tech('deepMining', 'industry', 1, [], {
    buildingOutput: { miningComplex: 0.25 },
    outpostOutput: 0.25,
  }),
  fusionReactors: tech('fusionReactors', 'industry', 2, ['automation'], {
    heliumPerEnergy: 0.5,
  }),
  orbitalInfrastructure: tech('orbitalInfrastructure', 'industry', 2, ['automation'], {
    orbitSlots: 1,
  }),
  nanofactories: tech('nanofactories', 'industry', 3, ['fusionReactors'], {
    buildingOutput: { smelter: 0.25, electronicsFactory: 0.25 },
    orbitSlots: 1,
  }),
  // Trade
  tradeProtocols: tech('tradeProtocols', 'trade', 1, [], {
    contractSlots: 1,
    caravanSpeed: 0.25,
    surfaceSlots: 1,
  }),
  agrogenetics: tech('agrogenetics', 'trade', 1, [], {
    buildingOutput: { agroComplex: 0.25 },
    populationGrowth: 0.2,
  }),
  financialEngineering: tech('financialEngineering', 'trade', 2, ['tradeProtocols'], {
    conversionFee: 0.03,
  }),
  // Rules per good that trade on the exchange every cycle (#123, the author's idea).
  tradeAlgorithms: tech('tradeAlgorithms', 'trade', 2, ['tradeProtocols'], {
    autoTrade: true,
  }),
  interstellarFinance: tech('interstellarFinance', 'trade', 3, ['financialEngineering'], {
    seigniorage: 0.03,
  }),
  // Exploration
  scannerMk2: tech('scannerMk2', 'exploration', 1, []),
  appliedScience: tech('appliedScience', 'exploration', 1, [], {
    buildingOutput: { researchCenter: 0.25 },
  }),
  xenomaterials: tech('xenomaterials', 'exploration', 2, ['scannerMk2'], {
    exoticsYield: 1.5,
    buildingInputs: { electronicsFactory: { exotics: 0.5 } },
  }),
  scannerMk3: tech('scannerMk3', 'exploration', 3, ['xenomaterials'], { scanTime: 0.8 }),
  // A full star chart (#97): tier 3, so dear early and in the middle of a match.
  stellarCartography: tech('stellarCartography', 'exploration', 3, ['scannerMk2'], {
    revealMap: true,
  }),
  // Fleet and defence
  defenseSystems: tech('defenseSystems', 'fleet', 1, []),
  heavyHulls: tech('heavyHulls', 'fleet', 2, ['defenseSystems']),
  fleetLogistics: tech('fleetLogistics', 'fleet', 2, ['defenseSystems'], {
    fleetUpkeep: 0.75,
    fleetLimit: 2,
    formations: true,
  }),
  orbitalAssault: tech('orbitalAssault', 'fleet', 2, ['defenseSystems']),
  // Resilience
  stellarMeteorology: tech('stellarMeteorology', 'resilience', 1, [], {
    crisisWarningTicks: CRISIS_DIRECTOR.foresightTicks,
    projectPrestige: 0.2,
  }),
  magneticScreens: tech('magneticScreens', 'resilience', 2, ['stellarMeteorology'], {
    crisisDamage: 0.5,
  }),
  // The final technology: any two of tier 3.
  starControl: tech(
    'starControl',
    'final',
    'final',
    ['nanofactories', 'interstellarFinance', 'scannerMk3'],
    { megastructure: true },
    2,
  ),
};

export const RESEARCH = {
  /** Price of a technology by tier: science, and exotics paid when research starts. */
  cost: {
    1: { science: 30, exotics: 0 },
    2: { science: 80, exotics: 0 },
    3: { science: 150, exotics: 10 },
    final: { science: 300, exotics: 20 },
  } as Readonly<Record<TechTier, { readonly science: number; readonly exotics: number }>>,
  /** Technologies researched at once; the research center of level 2+ adds one. */
  parallel: 1,
  parallelWithResearchLevel2: 2,
  /** Diffusion (GDD §10): the science price × this for every other player who knows it. */
  diffusionFactor: 0.85,
  /** A technology fragment takes this share off a random unknown tier-1–2 technology... */
  fragmentDiscount: 0.3,
  /** ...up to this much on one technology. */
  maxFragmentDiscount: 0.6,
} as const;
